import React, { useEffect, useState } from 'react';
import { TrendingUp } from 'lucide-react';
import { ComparisonScoreboard } from './ComparisonScoreboard';
import { automationService, type SavedComparison } from '@/services/strategyService';

function readKey(): string | null {
  const q = (window.location.hash || '').split('?')[1];
  return q ? new URLSearchParams(q).get('key') : null;
}

/**
 * Public, unauthenticated page for an expiring comparison share link.
 *
 * Rendered for `#/shared-comparison?key=<token>` OUTSIDE the dashboard auth gate.
 * Resolves the token via the anonymous-safe by-share endpoint and renders the
 * scoreboard read-only (no per-run drill-down — scoreboard only).
 */
export const SharedComparisonPage: React.FC = () => {
  const [token, setToken] = useState<string | null>(() => readKey());
  const [data, setData] = useState<SavedComparison | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const onHash = () => setToken(readKey());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  useEffect(() => {
    if (!token) { setLoading(false); setError('This share link is missing its token.'); return; }
    let cancelled = false;
    setLoading(true); setError(null); setData(null);
    automationService.getComparisonByShare(token)
      .then(c => { if (!cancelled) setData(c); })
      .catch(() => { if (!cancelled) setError('This share link is invalid or has expired.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [token]);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-2">
          <div className="h-7 w-7 rounded-lg bg-gradient-to-br from-primary to-blue-600 flex items-center justify-center">
            <TrendingUp className="h-4 w-4 text-white" />
          </div>
          <div className="text-sm font-bold tracking-tight"><span>Trade</span><span className="text-primary">Execute</span></div>
          <span className="text-xs text-muted-foreground ml-2">shared comparison</span>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-4 py-6 space-y-4">
        {loading ? (
          <div className="text-sm text-muted-foreground">Loading comparison…</div>
        ) : error ? (
          <div className="text-sm text-red-500">{error}</div>
        ) : data ? (
          <>
            <h1 className="text-lg font-semibold">{data.name}</h1>
            <ComparisonScoreboard comparison={data} />
          </>
        ) : null}
      </main>
    </div>
  );
};

export default SharedComparisonPage;
